import { isBlankOnDuplicate } from '../config/elementOptionPolicy';
import { isConfirmationsElement } from './confirmationValues';

/**
 * What a duplicate may carry from the item it copies — THE rule, one
 * spelling on each side of the wire:
 *
 *   keepr-api utils/quickAddTemplates/validate.js copyableOnDuplicate
 *     (a copyAll duplicate profile, resolved on the API)
 *   this file (plain Duplicate, which the item form fills client-side, and
 *     the profile editor's Copy rows)
 *
 * Both repos' tests run the SAME case table
 * (src/utils/__tests__/duplicateCopy.test.js here,
 * test/duplicateProfiles.test.js there) — change one, change both.
 *
 * Never copied:
 *   - a driven element (`drivenFrom` — calculated, lookup- or related-
 *     driven): the server owns its value;
 *   - a server-numbered one (`sequence`): the copy gets its own number;
 *   - the account link (a `user` element with `options.identity`): one
 *     record per person, so a copy carrying it could only ever fail 409
 *     `account_already_linked`;
 *   - an element the card leaves blank on duplicate (`options.duplicate`);
 *   - a "Me too" count (`confirmations`, KPR-390): a copy starts with
 *     nobody's confirmation (here the form would otherwise SHOW the
 *     original's count on the new item until it is saved).
 *
 * Other attachments are item-level files, never element values: no copy has
 * them. A FILE ELEMENT's value is an element value (PR 10b): copied like any
 * other unless the element leaves it blank (G5 pre-sets "Leave blank when
 * duplicating" on a new file element) — the API binds each id to the copy on
 * the same bytes — and the navigation carries the source item's id and
 * `files` facts beside it, for the form's thumbnails.
 */
export const copyableOnDuplicate = element => !!element
	&& !(element.options?.drivenFrom || element.options?.sequence)
	&& !(element.dataType === 'user' && element.options?.identity)
	&& !isConfirmationsElement(element)
	&& !isBlankOnDuplicate(element);

/**
 * Why an element is not copied, in a sentence for the profile editor —
 * null when it is.
 */
export function notCopiedReason(element) {
	if (copyableOnDuplicate(element)) return null;
	if (isBlankOnDuplicate(element)) return 'Left blank when duplicating (set on the card).';
	if (element?.dataType === 'user' && element?.options?.identity) return 'Never copied: it links a record to one person.';
	if (isConfirmationsElement(element)) return 'Never copied: a copy starts with no confirmations.';
	return 'Never copied: the server sets it.';
}
